/* =========================================================================
   Aunty Cherish — Smart "Frequently bought together" (2026-06-14)
   Layers REAL co-purchase data on top of the curated shade tiles.

   - Reads Shopify's own recommendations (/recommendations/products.json,
     intent=related) for the cart's braid → reflects what people actually
     cross-buy, and adapts as the catalog/orders change.
   - FILTERED to margin-safe only: AC vendor + a braiding-hair product type,
     and never a resold third-party brand (Freetress, Supreme, etc.) or a
     low-value tool (latch hook, needle = empty type). So it can't surface
     thin-margin junk or sold-out items.
   - Appends those as tiles AFTER the curated shades (cart-complete-set.liquid),
     trimming the curated ones so the row stays ~8. If no safe recs, the
     curated shades stand alone (fallback / no-JS baseline).
   - Multi-variant recs (other lengths, curls) → "Choose" (open PDP to pick a
     shade). Single-variant → one-tap "Add" (reuses cart-complete-set.js handler).
   - Re-runs after every drawer re-render (refreshCart) via a MutationObserver.
   ========================================================================= */
(function () {
  'use strict';

  // Margin-safe gate. Types that ARE braiding hair (vs gels/relaxers/oils = empty/other type).
  var SAFE_TYPES = ['Pre-Stretched Braiding Hair', 'Braiding hair', 'Hair extension'];
  // Resold third-party brands to exclude by title (thin margin).
  var BRAND_BLOCK = ['freetress', 'supreme', 'x-pression', 'xpression', 'outre', 'sensationnel', 'darling', 'ampro', 'shake n go', 'shake-n-go', 'sensual', 'janet', 'ez braid'];
  // Always-show, margin-safe picks (founder-chosen), surfaced even when Shopify's recs don't include them.
  var PINNED = ['italy-curly-bulk-braids']; // human hair curls (100% human hair, in stock, fat margin)
  var MAX_RECS = 4;
  var ROW_TARGET = 8;

  function money(cents) { return '$' + (cents / 100).toFixed(2); }
  function getCart() { return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); }
  function fetchJSON(u) { return fetch(u, { headers: { Accept: 'application/json' } }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; }); }

  function isSafe(p, cartIds) {
    if (!p.available) return false;
    if (cartIds.indexOf(p.id) !== -1) return false;                       // not already in cart
    if ((p.vendor || '').toLowerCase().indexOf('aunty cherish') === -1) return false; // AC only
    if (SAFE_TYPES.indexOf(p.type) === -1) return false;                  // braiding-hair types only
    var t = (p.title || '').toLowerCase();
    for (var i = 0; i < BRAND_BLOCK.length; i++) { if (t.indexOf(BRAND_BLOCK[i]) !== -1) return false; }
    return true;
  }

  function imgSrc(fi, w) {
    var u = !fi ? '' : (typeof fi === 'string' ? fi : (fi.src || ''));
    if (!u) return '';
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function tileHTML(p) {
    var avail = (p.variants || []).filter(function (v) { return v.available; });
    var title = (p.title || '')
      .replace(/\s*\(.*?\)\s*/g, ' ')                     // drop "(20 strands/pack)"
      .replace(/Pre-Stretched Braiding Hair/ig, 'Braids') // shorten long braid titles
      .replace(/\s+/g, ' ').trim();
    var safeTitle = title.replace(/"/g, '');
    var img = imgSrc(p.featured_image, 200);
    var onetap = avail.length === 1;
    var add = onetap ? '<button type="button" class="cart-complete-set__add" data-ac-addpack="' + avail[0].id + '" aria-label="Add ' + safeTitle + '">+</button>' : '';
    var choose = onetap ? '' : '<span class="cart-complete-set__choose">Choose</span>';
    return '<div class="cart-complete-set__card cart-complete-set__card--rec" role="listitem">'
      + '<a href="' + p.url + '" class="cart-complete-set__card-link">'
      + '<img class="cart-complete-set__thumb" src="' + img + '" alt="' + safeTitle + '" loading="lazy" width="92" height="92">'
      + '<span class="cart-complete-set__name">' + title + '</span>'
      + '<span class="cart-complete-set__price text-color--opacity">' + money(p.price) + '</span>'
      + choose
      + '</a>' + add + '</div>';
  }

  function enhance() {
    var block = document.querySelector('[data-js-complete-set]');
    if (!block) return;
    var row = block.querySelector('.cart-complete-set__row');
    if (!row) return;

    getCart().then(function (cart) {
      if (!cart.items.length) return;
      var cartIds = cart.items.map(function (i) { return i.product_id; });
      var sig = cartIds.join(',');
      if (row.getAttribute('data-rec-sig') === sig) return; // already enhanced for this cart
      row.setAttribute('data-rec-sig', sig);                // claim early (prevents double-append races)

      var seed = (cart.items.filter(function (i) { return i.product_type === 'Pre-Stretched Braiding Hair'; })[0] || cart.items[0]).product_id;

      // Pull recommendations + any pinned products in parallel.
      var jobs = [fetchJSON('/recommendations/products.json?product_id=' + seed + '&limit=10&intent=related')];
      PINNED.forEach(function (h) { jobs.push(fetchJSON('/products/' + h + '.js')); });

      Promise.all(jobs).then(function (res) {
        var row2 = document.querySelector('[data-js-complete-set] .cart-complete-set__row');
        if (!row2 || row2.getAttribute('data-rec-sig') !== sig) return; // re-rendered meanwhile
        row2.querySelectorAll('.cart-complete-set__card--rec').forEach(function (e) { e.remove(); });

        var recJson = res[0] || {};
        var pinnedProducts = res.slice(1);
        var picked = [];
        var seen = {};

        // Pinned first — margin-safe by founder choice, only the available/not-in-cart ones.
        pinnedProducts.forEach(function (p) {
          if (p && p.available && cartIds.indexOf(p.id) === -1 && !seen[p.id]) { seen[p.id] = 1; picked.push(p); }
        });
        // Then filtered recommendations, skipping anything already picked.
        (recJson.products || []).forEach(function (p) {
          if (picked.length >= MAX_RECS || seen[p.id]) return;
          if (isSafe(p, cartIds)) { seen[p.id] = 1; picked.push(p); }
        });
        picked = picked.slice(0, MAX_RECS);
        if (!picked.length) return;

        row2.insertAdjacentHTML('beforeend', picked.map(tileHTML).join(''));
        // Keep the row tidy: trim curated shade tiles so total ≈ ROW_TARGET (always keep ≥2 shades).
        var curated = [].slice.call(row2.querySelectorAll('.cart-complete-set__card:not(.cart-complete-set__card--rec)'));
        var keep = Math.max(2, ROW_TARGET - picked.length);
        curated.forEach(function (c, i) { c.style.display = i < keep ? '' : 'none'; });
      }).catch(function () { if (row.getAttribute('data-rec-sig') === sig) row.removeAttribute('data-rec-sig'); });
    }).catch(function () {});
  }

  var timer = null;
  function schedule() { if (timer) clearTimeout(timer); timer = setTimeout(enhance, 250); }

  // Cart changed → drop the signature so recs re-evaluate for the new cart.
  document.addEventListener('cart-updated', function () {
    var row = document.querySelector('[data-js-complete-set] .cart-complete-set__row');
    if (row) row.removeAttribute('data-rec-sig');
    schedule();
  }, true);

  // Drawer re-renders (refreshCart) replace the row; re-append when a fresh, un-enhanced row appears.
  var host = document.getElementById('AjaxCartForm');
  if (host) {
    new MutationObserver(function () {
      var row = document.querySelector('[data-js-complete-set] .cart-complete-set__row');
      if (row && !row.hasAttribute('data-rec-sig')) schedule();
    }).observe(host, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
})();
